import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { getOnboardingStatus } from '../reducers/companies';
import './OnboardingChecklist.css';

const steps = [
  {
    key: 'account',
    icon: 'fas fa-user-check',
    title: 'Create your account',
    description: 'You\'re in! Your account is ready to go.',
    always: true, // always complete
  },
  {
    key: 'company',
    icon: 'fas fa-building',
    title: 'Set up your first company',
    description: 'Create a company and let AI generate your templates.',
    link: '/companies/new',
    cta: 'Create Company',
  },
  {
    key: 'client',
    icon: 'fas fa-user-plus',
    title: 'Add your first client',
    description: 'Add a client to start managing your relationships.',
    link: '/clients',
    cta: 'Add Client',
  },
  {
    key: 'appointment',
    icon: 'fas fa-calendar-check',
    title: 'Create your first appointment',
    description: 'Schedule an appointment with a client.',
    link: '/calendar',
    cta: 'Schedule Appointment',
  },
  {
    key: 'invoice',
    icon: 'fas fa-file-invoice-dollar',
    title: 'Create your first invoice',
    description: 'Send a professional invoice to a client.',
    link: '/invoices',
    cta: 'Create Invoice',
  },
  {
    key: 'mobile',
    icon: 'fas fa-mobile-alt',
    title: 'Download the mobile app',
    description: 'Manage your business on the go.',
    external: true,
    cta: 'Coming Soon',
  },
];

class OnboardingChecklist extends React.Component {
  state = {
    status: null, // { hasClient, hasAppointment, hasInvoice } from /companies/:id/onboarding
  };

  componentDidMount() {
    this.fetchStatus();
  }

  componentDidUpdate(prevProps) {
    if (this.props.selectedCompanyId !== prevProps.selectedCompanyId) {
      this.setState({ status: null });
      this.fetchStatus();
    }
  }

  fetchStatus = () => {
    if (!this.props.selectedCompanyId) return;
    this.props.dispatchGetOnboardingStatus().then((status) => {
      if (status && !status.error) this.setState({ status });
    });
  };

  getCompletionMap() {
    const { companies } = this.props;
    const status = this.state.status || {};
    return {
      account: true,
      company: companies && companies.length > 0,
      client: !!status.hasClient,
      appointment: !!status.hasAppointment,
      invoice: !!status.hasInvoice,
      mobile: false, // TODO: track this separately
    };
  }

  render() {
    // Wait for the status call before rendering, so completed steps don't flash unticked.
    // No company selected = brand new user: render immediately, all steps open.
    if (this.props.selectedCompanyId && !this.state.status) return null;

    const completion = this.getCompletionMap();
    const completedCount = Object.values(completion).filter(Boolean).length;
    const totalSteps = steps.length;
    const progress = Math.round((completedCount / totalSteps) * 100);

    // Don't show if everything except mobile is done
    if (completedCount >= totalSteps - 1) return null;

    // Find the next incomplete step
    const nextStep = steps.find(s => !completion[s.key]);

    return (
      <div className="onboarding-checklist" data-testid="onboarding-checklist">
        <div className="onboarding-header">
          <div>
            <h3><i className="fas fa-rocket"></i> Get Started with MoneyApp</h3>
            <p className="onboarding-subtitle">Complete these steps to set up your business</p>
          </div>
          {/* No dismiss — must complete the steps */}
        </div>

        <div className="onboarding-progress">
          <div className="onboarding-progress-bar">
            <div className="onboarding-progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <span className="onboarding-progress-text">{completedCount} of {totalSteps} complete</span>
        </div>

        <div className="onboarding-steps">
          {steps.map((step, index) => {
            const done = completion[step.key];
            const isNext = nextStep && nextStep.key === step.key;
            // A step is locked if any previous non-always step is incomplete
            const isLocked = !done && !step.always && steps.slice(0, index).some(
              (prev) => !prev.always && !completion[prev.key]
            );
            return (
              <div
                key={step.key}
                className={`onboarding-step ${done ? 'done' : ''} ${isNext ? 'next' : ''} ${isLocked ? 'locked' : ''}`}
              >
                <div className="onboarding-step-check">
                  {done ? (
                    <i className="fas fa-check-circle"></i>
                  ) : isLocked ? (
                    <i className="fas fa-lock"></i>
                  ) : (
                    <i className="far fa-circle"></i>
                  )}
                </div>
                <div className="onboarding-step-icon">
                  <i className={step.icon}></i>
                </div>
                <div className="onboarding-step-content">
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                </div>
                {!done && step.link && !isLocked && (
                  <Link to={step.link} className="btn btn-sm btn-primary onboarding-step-cta">
                    {step.cta}
                  </Link>
                )}
                {!done && step.external && !isLocked && (
                  <span className="btn btn-sm btn-outline-secondary onboarding-step-cta disabled">
                    {step.cta}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  companies: state.companies.companies,
  selectedCompanyId: state.companies.selectedCompanyId,
});

const mapDispatchToProps = {
  dispatchGetOnboardingStatus: () => getOnboardingStatus(),
};

export default connect(mapStateToProps, mapDispatchToProps)(OnboardingChecklist);
